import { transactionFeeDescription } from "~components/checkout/order/checkout.order.ServiceFees";
import { TaxCalculationMethod } from "~graphql/typed-document-nodes";
import { useOrganization } from "~hooks/useOrganization";
import { GetTotalsReturn } from "~features/checkout/legacy/helpers/getTotals";
import type { Order, LineItem, TaxApplied } from "~graphql/sdk";
import type { PickTypes } from "~lib/helpers/types";

export interface ServiceCost {
  title: string;
  subtitle?: string;
  price?: number;
}

export interface UseServiceCostsProps {
  order:
    | undefined
    | PickTypes<
        Order,
        {
          bookingFeeAmount: true;
          transferFee: true;
          taxCalculationMethod: true;
          totalTax: true;
          customTaxAmount: true;
          lineItems: {
            edges: {
              node: PickTypes<
                LineItem,
                {
                  ticketFeeAmount: true;
                  quantity: true;
                  stadiumLevyFee: true;
                }
              >;
            }[];
          };
          orderTicketCover: {
            totalFeeAmount: true;
          };
          tax: {
            tax: PickTypes<
              TaxApplied,
              {
                name: true;
                total: true;
              }
            >[];
          };
        }
      >;
  totals: GetTotalsReturn;
}

export function useServiceCosts({
  order,
  totals,
}: UseServiceCostsProps): ServiceCost[] {
  const { organization, t } = useOrganization();

  const fees: ServiceCost[] = [];

  if (!order) {
    return fees;
  }

  // Per ticket fee
  if (order.lineItems.edges.some(({ node }) => node.ticketFeeAmount > 0)) {
    const totalPerTicketFees = order.lineItems.edges.reduce(
      (acc, { node }) =>
        +(acc + node.ticketFeeAmount * node.quantity).toFixed(2),
      0
    );

    fees.push({
      title: "Ticket fees",
      price: totalPerTicketFees,
    });
  }

  // Stadium levy fee
  if (order.lineItems.edges.some(({ node }) => node.stadiumLevyFee > 0)) {
    const totalStadiumLevyFee = order.lineItems.edges.reduce(
      (acc, { node }) => +(acc + node.stadiumLevyFee).toFixed(2),
      0
    );

    fees.push({
      title: "Stadium levy fee",
      price: totalStadiumLevyFee,
    });
  }

  // Order level booking fee
  if (order.bookingFeeAmount) {
    fees.push({
      title: "Order handling fee",
      price: order.bookingFeeAmount,
    });
  }

  // Ticket transfers
  if (order.transferFee) {
    fees.push({
      title: "Transfer fee",
      price: order.transferFee,
    });
  }

  // Delivery (and SMS)
  if (totals.deliveryFee) {
    fees.push({
      title: "Delivery costs",
      price: totals.deliveryFee,
    });
  }

  // TX fees
  if (totals.transactionFee?.transactionFeeAmount) {
    fees.push({
      title: "Transaction costs",
      subtitle: transactionFeeDescription(
        totals.transactionFee,
        1,
        organization.currency
      ),
      price: totals.transactionFee.transactionFeeAmount,
    });
  }

  // Ticket cover
  if (order.orderTicketCover?.totalFeeAmount) {
    fees.push({
      title: "Refundable tickets",
      price: order.orderTicketCover.totalFeeAmount,
    });
  }

  // Custom tax
  if (order.customTaxAmount) {
    fees.push({
      title: organization.customTaxName ?? "Additional tax",
      price: order.customTaxAmount,
    });
  }

  // Event specific tax
  if (order.tax?.tax) {
    for (const tax of order.tax.tax) {
      if (tax.total > 0) {
        fees.push({
          title: tax.name,
          price: tax.total,
        });
      }
    }
  }

  // Organization specific tax
  if (
    order.taxCalculationMethod === TaxCalculationMethod.Exclusive &&
    order.totalTax > 0
  ) {
    fees.push({
      title: t("tax_label"),
      price: order.totalTax,
    });
  }

  return fees;
}
